import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Form, Badge, InputGroup, Spinner, Row, Col } from "react-bootstrap";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// Script loader helper for Google Maps
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      return resolve(window.google.maps);
    }
    const existing = document.getElementById("google-maps-script");
    if (existing) {
      if (window.google && window.google.maps) return resolve(window.google.maps);
      existing.addEventListener("load", () => resolve(window.google.maps));
      existing.addEventListener("error", (e) => reject(e));
      return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

const DEFAULT_CENTER = { lat: 26.9124, lng: 75.7873 }; // Jaipur default

const OfficeGoogleMapPicker = ({
  office = {},
  onChange,
  onClose,
}) => {
  const mapContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const geocoderRef = useRef(null);

  const [mapLoading, setMapLoading] = useState(true);
  const [isLocating, setIsLocating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [geocodingNotice, setGeocodingNotice] = useState("");

  const lat = office.latitude ?? "";
  const lng = office.longitude ?? "";
  const radius = Number(office.allowedRadiusMeters) > 0 ? Number(office.allowedRadiusMeters) : 100;

  // Google Geocoder Manual Search (Enter key or Search button)
  const handleManualSearch = (queryOverride) => {
    const q = (typeof queryOverride === "string" ? queryOverride : searchQuery).trim();
    if (!q) return;

    if (!geocoderRef.current && window.google && window.google.maps) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    if (!geocoderRef.current) return;

    setGeocodingNotice(`Searching "${q}"...`);
    geocoderRef.current.geocode({ address: q }, (results, status) => {
      setGeocodingNotice("");
      if (status === "OK" && results && results[0] && results[0].geometry) {
        const loc = results[0].geometry.location;
        const pLat = loc.lat();
        const pLng = loc.lng();

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat: pLat, lng: pLng });
          mapInstanceRef.current.setZoom(17);
        }
        if (markerRef.current) markerRef.current.setPosition({ lat: pLat, lng: pLng });
        if (circleRef.current) circleRef.current.setCenter({ lat: pLat, lng: pLng });

        const formatted = results[0].formatted_address || q;
        setSearchQuery(formatted);

        if (onChange) {
          onChange({
            latitude: Number(pLat).toFixed(7),
            longitude: Number(pLng).toFixed(7),
            address: formatted,
          });
        }
      } else {
        alert(`Location "${q}" not found. Please try another place name or landmark.`);
      }
    });
  };

  // Google Reverse Geocoder
  const reverseGeocode = useCallback(
    (targetLat, targetLng) => {
      if (!geocoderRef.current && window.google && window.google.maps) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      if (!geocoderRef.current) return;

      setGeocodingNotice("Detecting address...");
      geocoderRef.current.geocode(
        { location: { lat: targetLat, lng: targetLng } },
        (results, status) => {
          setGeocodingNotice("");
          if (status === "OK" && results && results[0]) {
            const detectedAddress = results[0].formatted_address;
            setSearchQuery((prev) => prev ? prev : detectedAddress);
            if (onChange) {
              onChange({
                latitude: Number(targetLat).toFixed(7),
                longitude: Number(targetLng).toFixed(7),
                address: detectedAddress,
              });
            }
          }
        }
      );
    },
    [onChange]
  );

  // Initialize Google Map
  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsScript(GOOGLE_MAPS_KEY)
      .then((googleMaps) => {
        if (!isMounted || !mapContainerRef.current) return;
        setMapLoading(false);

        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        const hasCoords = !isNaN(parsedLat) && !isNaN(parsedLng);

        const startPos = hasCoords
          ? { lat: parsedLat, lng: parsedLng }
          : DEFAULT_CENTER;
        const startZoom = hasCoords ? 17 : 15;

        // Initialize Map
        const map = new googleMaps.Map(mapContainerRef.current, {
          center: startPos,
          zoom: startZoom,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: googleMaps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: googleMaps.ControlPosition.TOP_RIGHT,
          },
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });

        // Initialize Marker
        const marker = new googleMaps.Marker({
          position: startPos,
          map: map,
          draggable: true,
          animation: googleMaps.Animation.DROP,
          title: office.name || "Office Location",
        });

        // Initialize Circle
        const circle = new googleMaps.Circle({
          strokeColor: "#0d6efd",
          strokeOpacity: 0.85,
          strokeWeight: 2,
          fillColor: "#0d6efd",
          fillOpacity: 0.18,
          map: map,
          center: startPos,
          radius: radius,
        });

        markerRef.current = marker;
        circleRef.current = circle;
        mapInstanceRef.current = map;
        geocoderRef.current = new googleMaps.Geocoder();

        // Marker Drag
        marker.addListener("dragend", (e) => {
          const newLat = e.latLng.lat();
          const newLng = e.latLng.lng();
          circle.setCenter({ lat: newLat, lng: newLng });
          if (onChange) {
            onChange({
              latitude: Number(newLat).toFixed(7),
              longitude: Number(newLng).toFixed(7),
            });
          }
          reverseGeocode(newLat, newLng);
        });

        // Map Click
        map.addListener("click", (e) => {
          const clickLat = e.latLng.lat();
          const clickLng = e.latLng.lng();
          marker.setPosition({ lat: clickLat, lng: clickLng });
          circle.setCenter({ lat: clickLat, lng: clickLng });
          if (onChange) {
            onChange({
              latitude: Number(clickLat).toFixed(7),
              longitude: Number(clickLng).toFixed(7),
            });
          }
          reverseGeocode(clickLat, clickLng);
        });

        // Google Places Autocomplete
        if (searchInputRef.current && googleMaps.places) {
          const autocomplete = new googleMaps.places.Autocomplete(
            searchInputRef.current,
            {
              fields: ["geometry", "formatted_address", "name"],
            }
          );
          autocomplete.bindTo("bounds", map);

          autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (!place || !place.geometry || !place.geometry.location) {
              // Fallback to manual geocoder search if user typed custom text and hit enter
              const val = searchInputRef.current?.value || searchQuery;
              if (val) handleManualSearch(val);
              return;
            }

            const pLat = place.geometry.location.lat();
            const pLng = place.geometry.location.lng();

            map.setCenter({ lat: pLat, lng: pLng });
            map.setZoom(17);
            marker.setPosition({ lat: pLat, lng: pLng });
            circle.setCenter({ lat: pLat, lng: pLng });

            const formatted = place.formatted_address || place.name || "";
            setSearchQuery(formatted);

            if (onChange) {
              onChange({
                latitude: Number(pLat).toFixed(7),
                longitude: Number(pLng).toFixed(7),
                address: formatted,
              });
            }
          });
        }

        // Auto-detect GPS if coords empty
        if (!hasCoords && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const uLat = pos.coords.latitude;
              const uLng = pos.coords.longitude;
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setCenter({ lat: uLat, lng: uLng });
                mapInstanceRef.current.setZoom(17);
              }
              if (markerRef.current) markerRef.current.setPosition({ lat: uLat, lng: uLng });
              if (circleRef.current) circleRef.current.setCenter({ lat: uLat, lng: uLng });
              if (onChange) {
                onChange({
                  latitude: Number(uLat).toFixed(7),
                  longitude: Number(uLng).toFixed(7),
                });
              }
              reverseGeocode(uLat, uLng);
            },
            () => {},
            { timeout: 7000, enableHighAccuracy: true }
          );
        }
      })
      .catch((err) => {
        console.error("Google Maps load error:", err);
        setMapLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []); // Run on mount

  // Sync circle radius when prop changes
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setRadius(radius);
    }
  }, [radius]);

  // Sync marker position if coords change outside
  useEffect(() => {
    const pLat = parseFloat(lat);
    const pLng = parseFloat(lng);
    if (!isNaN(pLat) && !isNaN(pLng) && mapInstanceRef.current && markerRef.current && circleRef.current) {
      const pos = { lat: pLat, lng: pLng };
      markerRef.current.setPosition(pos);
      circleRef.current.setCenter(pos);
    }
  }, [lat, lng]);

  // Locate Current GPS
  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      alert("Location is not supported in this browser");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsLocating(false);
        const userLat = pos.coords.latitude;
        const userLng = pos.coords.longitude;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo({ lat: userLat, lng: userLng });
          mapInstanceRef.current.setZoom(17);
        }
        if (markerRef.current) markerRef.current.setPosition({ lat: userLat, lng: userLng });
        if (circleRef.current) circleRef.current.setCenter({ lat: userLat, lng: userLng });
        if (onChange) {
          onChange({
            latitude: Number(userLat).toFixed(7),
            longitude: Number(userLng).toFixed(7),
          });
        }
        reverseGeocode(userLat, userLng);
      },
      (err) => {
        setIsLocating(false);
        alert("Please allow location permissions in your browser.");
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  return (
    <div className="mt-3 p-3 bg-white rounded border shadow-sm position-relative">
      {/* Top Header of Inline Map */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 pb-2 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-map-marked-alt text-primary"></i>
          <strong className="small text-dark">
            Interactive Map: {office.name || "Select Office Location"}
          </strong>
          {lat && lng && (
            <Badge bg="success" className="d-inline-flex align-items-center gap-1" style={{ fontSize: 10 }}>
              <i className="fas fa-check"></i> Location Pinned
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            className="btn-sm py-0 px-2"
            style={{ fontSize: 12, height: 28 }}
            onClick={onClose}
          >
            <i className="fas fa-check me-1 text-success"></i> Done
          </Button>
        </div>
      </div>

      {/* Google Places Search & Locate Bar */}
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <InputGroup size="sm" style={{ flex: "1 1 240px", minWidth: "200px" }}>
          <InputGroup.Text className="bg-light">
            <i className="fab fa-google text-primary me-1"></i>
            <i className="fas fa-search text-muted"></i>
          </InputGroup.Text>
          <Form.Control
            ref={searchInputRef}
            type="text"
            placeholder="Search place, building, or landmark with Google Places..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleManualSearch();
              }
            }}
            autoComplete="off"
          />
        </InputGroup>

        <Button
          size="sm"
          variant="primary"
          type="button"
          onClick={() => handleManualSearch()}
          title="Search Place"
          className="d-flex align-items-center gap-1 shadow-sm px-3"
          style={{ height: "31px", whiteSpace: "nowrap" }}
        >
          <i className="fas fa-search"></i> Search
        </Button>

        <Button
          size="sm"
          variant="outline-secondary"
          type="button"
          onClick={handleLocateMe}
          disabled={isLocating}
          title="Detect My Current GPS Location"
          className="d-flex align-items-center gap-1 px-3"
          style={{ height: "31px", whiteSpace: "nowrap" }}
        >
          {isLocating ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <>
              <i className="fas fa-crosshairs text-primary"></i> My Location
            </>
          )}
        </Button>
      </div>

      {/* Map Canvas */}
      <div className="position-relative rounded overflow-hidden border mb-2" style={{ height: 320 }}>
        {mapLoading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light" style={{ zIndex: 10 }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" size="sm" />
              <div className="small text-muted mt-1" style={{ fontSize: 11 }}>Loading Google Maps...</div>
            </div>
          </div>
        )}

        <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />

        {/* Floating map hint */}
        <div
          className="position-absolute bottom-0 start-50 translate-middle-x mb-2 px-3 py-1 rounded-pill bg-dark text-white shadow-sm small"
          style={{
            zIndex: 5,
            opacity: 0.9,
            pointerEvents: "none",
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
        >
          <i className="fas fa-hand-pointer me-1 text-warning"></i>
          Click map or drag the pin to set exact office gate/building
        </div>

        {/* Floating geocode indicator */}
        {geocodingNotice && (
          <div
            className="position-absolute top-0 end-0 m-2 px-2 py-1 bg-white border rounded shadow-sm small text-primary"
            style={{ zIndex: 5, fontSize: 11 }}
          >
            <Spinner size="sm" animation="border" className="me-1" />
            {geocodingNotice}
          </div>
        )}
      </div>

      {/* Quick Radius Selection & Coordinates Info */}
      <Row className="g-2 align-items-center pt-1">
        <Col md={7}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="small text-muted fw-semibold">Radius Preset:</span>
            {[50, 100, 200, 500].map((rVal) => (
              <Badge
                key={rVal}
                bg={radius === rVal ? "primary" : "light"}
                text={radius === rVal ? "white" : "dark"}
                className="border"
                style={{ cursor: "pointer", fontSize: 11, padding: "4px 8px" }}
                onClick={() => onChange && onChange({ allowedRadiusMeters: rVal })}
              >
                {rVal}m
              </Badge>
            ))}
          </div>
        </Col>
        <Col md={5} className="text-end">
          <small className="text-muted" style={{ fontSize: 11 }}>
            {lat && lng ? (
              <span className="text-success fw-semibold">
                <i className="fas fa-map-pin me-1"></i>
                {lat}, {lng}
              </span>
            ) : (
              <span className="text-secondary">Click on map to drop pin</span>
            )}
          </small>
        </Col>
      </Row>
    
      {/* Google Places Autocomplete dropdown z-index inside modal & dark mode styling */}
      <style>{`
        .pac-container {
          z-index: 100000 !important;
          font-family: inherit;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          margin-top: 4px;
          pointer-events: auto !important;
        }
        .pac-item {
          padding: 8px 12px;
          cursor: pointer;
        }
        .pac-item:hover {
          background-color: #f1f5f9;
        }
        [data-theme-version="dark"] .pac-container {
          background-color: #1e1e2d !important;
          border-color: rgba(255, 255, 255, 0.15) !important;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6) !important;
        }
        [data-theme-version="dark"] .pac-item {
          color: #f1f5f9 !important;
          border-top-color: rgba(255, 255, 255, 0.08) !important;
        }
        [data-theme-version="dark"] .pac-item:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        [data-theme-version="dark"] .pac-item-query {
          color: #ffffff !important;
        }
        [data-theme-version="dark"] .pac-matched {
          color: #886cff !important;
        }
      `}</style>
    </div>
  );
};

export default OfficeGoogleMapPicker;
